import { Component, computed, DestroyRef, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { IconName, provideIcons } from '@ng-icons/core';
import {
  lucideArchive,
  lucideArrowLeft,
  lucideBookOpen,
  lucideCircle,
  lucideCircleCheck,
  lucideFilePenLine,
  lucideGitBranch,
  lucideLayers,
} from '@ng-icons/lucide';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmSidebarImports, HlmSidebarService } from '@spartan-ng/helm/sidebar';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { toast } from 'ngx-sonner';
import { filter } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { ConfirmDialogComponent } from '../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { ToastrSuccessComponent } from '../../../shared/components/toastr/toastr-success.component';
import { ReferentielMetierUpdateRequest } from '../../requests/update-request';
import { ReferentielStore } from '../../../administration/gestion-référentiels/store/referentiel.store';

@Component({
  selector: 'app-form-layout',
  imports: [HlmIconImports, HlmButtonImports, RouterModule, HlmTabsImports, HlmSidebarImports, HlmTooltipImports],
  providers: [
    provideIcons({
      lucideBookOpen,
      lucideFilePenLine,
      lucideLayers,
      lucideGitBranch,
      lucideArchive,
      lucideCircle,
      lucideArrowLeft,
      lucideCircleCheck,
    }),
  ],
  templateUrl: './form-layout.component.html',
})
export class FormLayoutComponent implements OnInit, OnDestroy {
  readonly store = inject(ReferentielStore);
  readonly sidebarService = inject(HlmSidebarService);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #canEditService = inject(CanEditService);

  readonly roles = input.required<string[]>();
  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = computed(() => this.store.selectedEntity());
  readonly isSidebarOpen = computed(() => this.sidebarService.open());

  readonly peutCompleter = computed(() =>
    this.#canEditService.check(
      this.referentiel()?.equipeResponsable,
      this.roles(),
      LivrablePrincipal.REFERENTIEL_METIER,
      this.cartographie(),
      this.referentiel(),
    ),
  );

  readonly links = [
    { label: 'Général', href: 'général' },
    { label: 'Périmètre', href: 'périmètre' },
    { label: 'Processus', href: 'processus' },
  ];

  protected readonly _items = computed<{ title: string; url: string; icon: IconName }[]>(() => [
    {
      title: 'Général',
      url: 'général',
      icon: 'lucideBookOpen',
    },
    {
      title: 'Périmètre',
      url: 'périmètre',
      icon: 'lucideFilePenLine',
    },
    {
      title: 'Processus',
      url: 'processus',
      icon: 'lucideLayers',
    },
    {
      title: 'Interrelations',
      url: 'interrelations',
      icon: 'lucideGitBranch',
    },
    {
      title: 'Ressources professionnelles',
      url: 'ressources-professionnelles',
      icon: 'lucideArchive',
    },
  ]);

  activeTab = signal('');

  ngOnInit(): void {
    this.activeTab.set(decodeURI(this.#router.url.split('/')[5]));
  }

  ngOnDestroy(): void {
    toast.dismiss();
  }

  completer(estComplete: boolean) {
    const referentiel = this.referentiel();
    if (!referentiel) return;

    const toastrId = uuidv7();

    const dataDialog = {
      titre: !estComplete ? 'Complétion du référentiel' : 'Retour à la modification',
      contenu: !estComplete
        ? `Confirmez-vous la complétion de ce référentiel ?`
        : 'Confirmez-vous le retour à la modification de ce référentiel',
    };

    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: dataDialog,
    });

    const référentielModifié: ReferentielMetierUpdateRequest = {
      estComplete: !estComplete,
    };

    dialogRef.closed$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((result) => result),
      )
      .subscribe(() => {
        this.store.modifier({
          id: referentiel.id,
          data: référentielModifié,
          next: () => {
            toast.custom(ToastrSuccessComponent, {
              componentProps: { message: 'Le référentiel est marqué comme complété', id: toastrId },
              id: toastrId,
            });
          },
        });
      });
  }
}
